import { createContext, useContext, useEffect, useState } from 'react';
import useAppointmentActions from '../hooks/APIHooks/appointments/useAppointmentActions';
import useReferralActions from '../hooks/APIHooks/users/useReferralActions';
import { AppointmentRequest } from '../types/appointmentRequests';
import { useAuthContext } from './AuthProvider';

// datatype of the context declared here
interface IAppointmentListContext {
  isAppointmentListLoading: boolean;
  currentPage: number;
  appointmentType: string;
  appointmentList: Array<AppointmentRequest>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setAppointmentType: React.Dispatch<React.SetStateAction<string>>;
  setAppointmentList: React.Dispatch<
    React.SetStateAction<Array<AppointmentRequest>>
  >;
}

// creating the context here
const AppointmentListContext = createContext<IAppointmentListContext | null>(
  null
);

const AppointmentListProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // integration of react hooks here
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [appointmentType, setAppointmentType] = useState<string>('all');
  const [appointmentList, setAppointmentList] = useState<
    Array<AppointmentRequest>
  >([]);

  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();

  // integration of custom API hooks here
  const {
    getAppointmentsByClientIdHandler,
    getAppointmentsByAssociateIdHandler,
  } = useAppointmentActions(
    '',
    currentUserType === 'user' ? currentUser?.id : undefined,
    currentUserType === 'client' ? currentUser?.id : undefined,
    appointmentType !== 'referrals' ? appointmentType : undefined
  );

  const { getClientReferralsByClientId } = useReferralActions(
    '',
    undefined,
    appointmentType === 'referrals' && currentUserType === 'client'
      ? currentUser?.id
      : undefined
  );

  console.log(
    'CLIENT REFERRALS LIST: ',
    getClientReferralsByClientId.data?.data
  );

  // refetching appointment list based on filter text here
  useEffect(() => {
    if (currentUser?.id) {
      if (appointmentType !== 'referrals') {
        if (currentUserType === 'user') {
          console.log('CALLING REFETCH!!');
          getAppointmentsByAssociateIdHandler.refetch();
          setCurrentPage(1);
        } else if (currentUserType === 'client') {
          getAppointmentsByClientIdHandler.refetch();
          setCurrentPage(1);
        }
      } else {
        getClientReferralsByClientId.refetch();
        setCurrentPage(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentType, currentUserType]);

  // setting appointment list data into states depending on user type here
  useEffect(() => {
    if (currentUserType === 'user') {
      if (getAppointmentsByAssociateIdHandler.data?.is_success) {
        setAppointmentList(getAppointmentsByAssociateIdHandler.data.data);
      }
    } else {
      if (getAppointmentsByClientIdHandler.data?.is_success) {
        setAppointmentList(getAppointmentsByClientIdHandler.data.data);
      }

      if (getClientReferralsByClientId.data?.is_success) {
        setAppointmentList(getClientReferralsByClientId.data.data);
      }
    }
  }, [
    currentUserType,
    getAppointmentsByAssociateIdHandler.data,
    getAppointmentsByClientIdHandler.data,
    getClientReferralsByClientId.data,
  ]);

  console.log(
    'APPOINTMENT BY CLIENT: ',
    getAppointmentsByClientIdHandler.data?.data
  );

  console.log(
    'APPOINTMENT BY ASSOCIATE: ',
    getAppointmentsByAssociateIdHandler.data?.data
  );

  console.log('APPOINTMENT LIST: ', appointmentList);

  // rendering the context provider here
  return (
    <AppointmentListContext.Provider
      value={{
        isAppointmentListLoading:
          currentUserType === 'user'
            ? getAppointmentsByAssociateIdHandler.isFetching
            : appointmentType === 'referrals'
            ? getClientReferralsByClientId.isFetching
            : getAppointmentsByClientIdHandler.isFetching,
        currentPage,
        appointmentType,
        appointmentList,
        setCurrentPage,
        setAppointmentType,
        setAppointmentList,
      }}
    >
      {children}
    </AppointmentListContext.Provider>
  );
};

export default AppointmentListProvider;

// custom hook to access appointment list context declared here
export const useAppointmentListContext = () => {
  const context = useContext(AppointmentListContext);

  if (!context) {
    throw new Error(
      'useAppointmentListContext must be within AppointmentListProvider!'
    );
  }

  return context;
};
